// src/components/academic/AcademicClasses.js
import React, { useState, useEffect } from 'react';
import {
  Autocomplete, Box, Card, CardContent, TextField,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, CircularProgress, Tabs, Tab, Chip, Button, useTheme, useMediaQuery
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { academicAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const AcademicClasses = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.ACADEMIC.CLASSES, ACTIONS.READ);
  const canCreate = can(RESOURCES.ACADEMIC.CLASSES, ACTIONS.CREATE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [levels, setLevels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('class');
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    level_id: '',
    year_of_study: '',
    capacity: ''
  });
  const [streamFormData, setStreamFormData] = useState({
    name: ''
  });
  const [classStreamFormData, setClassStreamFormData] = useState({
    class_id: '',
    stream_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogLoading, setDialogLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (canRead) fetchData();
  }, [canRead]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, streamsRes, classStreamsRes, levelsRes] = await Promise.all([
        academicAPI.getClasses(),
        academicAPI.getStreams(),
        academicAPI.getClassStreams(),
        academicAPI.getLevels()
      ]);
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : classesRes.data.data || []);
      setStreams(Array.isArray(streamsRes.data) ? streamsRes.data : streamsRes.data.data || []);
      setClassStreams(Array.isArray(classStreamsRes.data) ? classStreamsRes.data : classStreamsRes.data.data || []);
      setLevels(Array.isArray(levelsRes.data) ? levelsRes.data : levelsRes.data.data || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);
    setErrors({});
    try {
      if (modalType === 'class') {
        await academicAPI.createClass(formData);
        setSnackbar({ open: true, message: 'Class added successfully!', severity: 'success' });
      } else if (modalType === 'stream') {
        await academicAPI.createStream(streamFormData);
        setSnackbar({ open: true, message: 'Stream added successfully!', severity: 'success' });
      } else if (modalType === 'classStream') {
        await academicAPI.createClassStream(classStreamFormData);
        setSnackbar({ open: true, message: 'Class Stream added successfully!', severity: 'success' });
      }
      setShowModal(false);
      setFormData({ name: '', level_id: '', year_of_study: '', capacity: '' });
      setStreamFormData({ name: '' });
      setClassStreamFormData({ class_id: '', stream_id: '' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setSnackbar({ open: true, message: 'Please fix the validation errors', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || 'Error creating item', severity: 'error' });
      }
    } finally {
      setDialogLoading(false);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setErrors({});
    setShowModal(true);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTableHeaders = () => {
    if (activeTab === 0) {
      // Classes headers
      return (
        <TableRow>
          <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Name</TableCell>
          <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Level</TableCell>
          <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Year</TableCell>
          <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Capacity</TableCell>
        </TableRow>
      );
    } else if (activeTab === 1) {
      // Streams headers
      return (
        <TableRow>
          <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Name</TableCell>
        </TableRow>
      );
    } else {
      // Class Streams headers
      return (
        <TableRow>
          <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Name</TableCell>
        </TableRow>
      );
    }
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (activeTab === 0) {
      // Classes Tab
      if (classes.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={4} align="center" sx={{ py: 8, color: 'text.secondary' }}>
              No classes found. Click the + icon to add a class.
            </TableCell>
          </TableRow>
        );
      }
      return classes.map((cls) => (
        <TableRow key={cls.id} hover>
          <TableCell sx={{ fontWeight: 600 }}>{cls.name}</TableCell>
          <TableCell>{levels.find(l => l.id === cls.level_id)?.name || '-'}</TableCell>
          <TableCell>{cls.year_of_study}</TableCell>
          <TableCell>{cls.capacity || '-'}</TableCell>
        </TableRow>
      ));
    } else if (activeTab === 1) {
      // Streams Tab
      if (streams.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={1} align="center" sx={{ py: 8, color: 'text.secondary' }}>
              No streams found. Click the + icon to add a stream.
            </TableCell>
          </TableRow>
        );
      }
      return streams.map((stream) => (
        <TableRow key={stream.id} hover>
          <TableCell sx={{ fontWeight: 600 }}>{stream.name}</TableCell>
        </TableRow>
      ));
    } else {
      // Class Streams Tab
      if (classStreams.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={1} align="center" sx={{ py: 8, color: 'text.secondary' }}>
              No class streams found. Click the + icon to add a class stream.
            </TableCell>
          </TableRow>
        );
      }
      return classStreams.map((cs) => (
        <TableRow key={cs.id} hover>
          <TableCell sx={{ fontWeight: 600 }}>{cs.display_name}</TableCell>
        </TableRow>
      ));
    }
  };

  const getAddButtonTooltip = () => {
    if (activeTab === 0) return 'Add Class';
    if (activeTab === 1) return 'Add Stream';
    return 'Add Class Stream';
  };

  const getModalType = () => {
    if (activeTab === 0) return 'class';
    if (activeTab === 1) return 'stream';
    return 'classStream';
  };

  return (
    <Box>
      {!canRead && <Alert severity="error" sx={{ mb: 2 }}>You do not have permission to view classes.</Alert>}
      <ModulePageHeader
        title="Classes & Streams Management"
        subtitle="Manage all academic classes and streams in one unified view"
        actions={canCreate ? [
          <Button
            key="add-item"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openModal(getModalType())}
            fullWidth={isMobile}
          >
            {getAddButtonTooltip()}
          </Button>
        ] : []}
      />

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ flexGrow: 1 }}>
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>Classes</span>
                    <Chip label={classes.length} size="small" color="primary" />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>Streams</span>
                    <Chip label={streams.length} size="small" color="secondary" />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>Class Streams</span>
                    <Chip label={classStreams.length} size="small" color="success" />
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                {renderTableHeaders()}
              </TableHead>
              <TableBody>
                {renderTableContent()}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalType === 'class' ? 'Add New Class' : modalType === 'stream' ? 'Add New Stream' : 'Add New Class Stream'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {modalType === 'class' ? (
              <>
                <TextField
                  label="Class Name"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  autoFocus
                  error={!!errors.name}
                  helperText={errors.name?.[0]}
                />
                <Autocomplete
                  fullWidth
                  options={levels}
                  value={levels.find((level) => Number(level.id) === Number(formData.level_id)) || null}
                  onChange={(_, value) => setFormData({ ...formData, level_id: value?.id || '' })}
                  getOptionLabel={(option) => option.name || 'Level'}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      label="Level"
                      required
                      error={!!errors.level_id}
                      helperText={errors.level_id?.[0]}
                    />
                  )}
                />
                <TextField
                  label="Year of Study"
                  type="number"
                  value={formData.year_of_study}
                  onChange={e => setFormData({ ...formData, year_of_study: e.target.value })}
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.year_of_study}
                  helperText={errors.year_of_study?.[0]}
                />
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={e => setFormData({ ...formData, capacity: e.target.value })}
                  fullWidth
                  margin="normal"
                  error={!!errors.capacity}
                  helperText={errors.capacity?.[0]}
                />
              </>
            ) : modalType === 'stream' ? (
              <TextField
                label="Stream Name"
                value={streamFormData.name}
                onChange={e => setStreamFormData({ ...streamFormData, name: e.target.value })}
                fullWidth
                margin="normal"
                required
                autoFocus
                error={!!errors.name}
                helperText={errors.name?.[0]}
              />
            ) : (
              <>
                <Autocomplete
                  fullWidth
                  options={classes}
                  value={classes.find((cls) => Number(cls.id) === Number(classStreamFormData.class_id)) || null}
                  onChange={(_, value) => setClassStreamFormData({ ...classStreamFormData, class_id: value?.id || '' })}
                  getOptionLabel={(option) => option.name || option.class_name || 'Class'}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      label="Class"
                      required
                      error={!!errors.class_id}
                      helperText={errors.class_id?.[0]}
                    />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={streams}
                  value={streams.find((stream) => Number(stream.id) === Number(classStreamFormData.stream_id)) || null}
                  onChange={(_, value) => setClassStreamFormData({ ...classStreamFormData, stream_id: value?.id || '' })}
                  getOptionLabel={(option) => option.name || option.stream_name || 'Stream'}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      label="Stream"
                      required
                      error={!!errors.stream_id}
                      helperText={errors.stream_id?.[0]}
                    />
                  )}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowModal(false)} disabled={dialogLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
          >
            {dialogLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AcademicClasses;
